/**
 * Weddings API: TanStack Query hooks wrapping Supabase CRUD for the
 * `weddings` table. Components should never call `supabase.from('weddings')`
 * directly — always go through these hooks, so caching/invalidation stays
 * consistent across the app.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { Wedding } from '../domain/types';
import type { Database } from '../lib/database.types';
import { useDuplicateLayout } from './layouts';

type WeddingRow = Database['public']['Tables']['weddings']['Row'];

function rowToWedding(row: WeddingRow): Wedding {
  return {
    id: row.id,
    createdBy: row.created_by,
    coupleNames: row.couple_names,
    eventDate: row.event_date,
    room: row.room,
    guestCount: row.guest_count,
    headTableType: row.head_table_type,
    headTablePartySize: row.head_table_party_size,
    layoutStyle: row.layout_style,
    status: row.status,
    isTemplateReference: row.is_template_reference,
    shareToken: row.share_token,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const WEDDINGS_KEY = ['weddings'] as const;

/** Fetches ONLY normal (non-template) weddings for the Dashboard.
 * Always filtered to is_template_reference = false so templates saved
 * from the editor never pollute the Weddings list. */
export function useWeddings() {
  return useQuery({
    queryKey: [...WEDDINGS_KEY, 'normal'],
    queryFn: async (): Promise<Wedding[]> => {
      const { data, error } = await supabase
        .from('weddings')
        .select('*')
        .eq('is_template_reference', false)
        .order('event_date', { ascending: true });

      if (error) throw error;
      return data.map(rowToWedding);
    },
  });
}

/** Fetches ONLY saved templates for the Template Library.
 * Always filtered to is_template_reference = true. */
export function useTemplateWeddings() {
  return useQuery({
    queryKey: [...WEDDINGS_KEY, 'templates'],
    queryFn: async (): Promise<Wedding[]> => {
      const { data, error } = await supabase
        .from('weddings')
        .select('*')
        .eq('is_template_reference', true)
        .order('event_date', { ascending: false });

      if (error) throw error;
      return data.map(rowToWedding);
    },
  });
}

export function useWedding(weddingId: string | undefined) {
  return useQuery({
    queryKey: [...WEDDINGS_KEY, weddingId],
    queryFn: async (): Promise<Wedding> => {
      const { data, error } = await supabase
        .from('weddings')
        .select('*')
        .eq('id', weddingId!)
        .single();

      if (error) throw error;
      return rowToWedding(data);
    },
    enabled: !!weddingId,
  });
}

export interface CreateWeddingInput {
  coupleNames: string;
  eventDate: string;
  room: Wedding['room'];
  guestCount: number;
  headTableType: Wedding['headTableType'];
  headTablePartySize: number | null;
  layoutStyle: Wedding['layoutStyle'];
  createdBy: string;
  isTemplateReference?: boolean;
}

export function useCreateWedding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateWeddingInput): Promise<Wedding> => {
      const { data, error } = await supabase
        .from('weddings')
        .insert({
          couple_names: input.coupleNames,
          event_date: input.eventDate,
          room: input.room,
          // Always explicitly set is_template_reference — never rely on the DB
          // column default. Omitting it caused every new wedding to inherit
          // whatever the default is, which made normal weddings appear in the
          // Template Library when the default happened to be true.
          is_template_reference: input.isTemplateReference ?? false,
          guest_count: input.guestCount,
          head_table_type: input.headTableType,
          head_table_party_size: input.headTablePartySize,
          layout_style: input.layoutStyle,
          created_by: input.createdBy,
        })
        .select()
        .single();

      if (error) throw error;
      return rowToWedding(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEDDINGS_KEY });
    },
  });
}

export interface UpdateWeddingInput {
  id: string;
  coupleNames?: string;
  eventDate?: string;
  room?: Wedding['room'];
  guestCount?: number;
  headTableType?: Wedding['headTableType'];
  headTablePartySize?: number | null;
  layoutStyle?: Wedding['layoutStyle'];
  status?: Wedding['status'];
  isTemplateReference?: boolean;
}

export function useUpdateWedding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateWeddingInput): Promise<Wedding> => {
      const { id, ...rest } = input;
      const { data, error } = await supabase
        .from('weddings')
        .update({
          ...(rest.coupleNames !== undefined && { couple_names: rest.coupleNames }),
          ...(rest.eventDate !== undefined && { event_date: rest.eventDate }),
          ...(rest.room !== undefined && { room: rest.room }),
          ...(rest.guestCount !== undefined && { guest_count: rest.guestCount }),
          ...(rest.headTableType !== undefined && { head_table_type: rest.headTableType }),
          ...(rest.headTablePartySize !== undefined && { head_table_party_size: rest.headTablePartySize }),
          ...(rest.layoutStyle !== undefined && { layout_style: rest.layoutStyle }),
          ...(rest.status !== undefined && { status: rest.status }),
          ...(rest.isTemplateReference !== undefined && { is_template_reference: rest.isTemplateReference }),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return rowToWedding(data);
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: WEDDINGS_KEY });
      queryClient.invalidateQueries({ queryKey: [...WEDDINGS_KEY, updated.id] });
    },
  });
}

/**
 * Admin-only in practice (enforced by RLS — see weddings_delete_admin_only
 * policy). The UI should hide this action entirely for coordinators rather
 * than show it disabled, but this hook makes no assumption about who's
 * calling it; the database is the real gate.
 */
export function useDeleteWedding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weddingId: string): Promise<void> => {
      const { error } = await supabase.from('weddings').delete().eq('id', weddingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEDDINGS_KEY });
    },
  });
}

/**
 * Duplicates an entire wedding — both the wedding record (couple names,
 * room, guest count, style, head table config) and its layout's full
 * object graph — in one action, for the Dashboard's "Duplicate Wedding"
 * button. This is deliberately a thin orchestration over the two
 * existing primitives (createWedding + duplicateLayout) rather than new
 * logic: it's the exact same two-step pattern TemplateLibraryPage.tsx
 * already uses for "duplicate as a starting point for a new wedding,"
 * just with the source wedding's own name preserved (plus a "(Copy)"
 * suffix) instead of a generic "from template" placeholder — duplicating
 * a specific wedding from the Dashboard is "make a copy of this," not
 * "use this as inspiration for something unrelated."
 */
export function useDuplicateWedding() {
  const queryClient = useQueryClient();
  const createWedding = useCreateWedding();
  const duplicateLayout = useDuplicateLayout();

  return useMutation({
    mutationFn: async ({
      source,
      createdBy,
    }: {
      source: Wedding;
      createdBy: string;
    }): Promise<Wedding> => {
      const newWedding = await createWedding.mutateAsync({
        coupleNames: `${source.coupleNames} (Copy)`,
        eventDate: source.eventDate,
        room: source.room,
        guestCount: source.guestCount,
        headTableType: source.headTableType,
        headTablePartySize: source.headTablePartySize,
        layoutStyle: source.layoutStyle,
        createdBy,
      });

      await duplicateLayout.mutateAsync({
        sourceWeddingId: source.id,
        targetWeddingId: newWedding.id,
      });

      return newWedding;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEDDINGS_KEY });
    },
  });
}
